import { useEffect, useMemo, useState } from 'react';
import { PaletteMode, useMediaQuery } from '@mui/material';
import { createTheme } from '@mui/material/styles';

import { LocalStorageEnums } from 'src/shared/enums';
import { getAppBrandConfigs, getDesignComponents, getDesignPalette } from 'src/shared/utils';

import { useLocalStorage } from './useLocalStorage';

export const useAppTheme = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { value: storedThemeMode, setValue } = useLocalStorage<PaletteMode>(LocalStorageEnums.THEME_MODE);
  const browserPreferredMode = prefersDarkMode ? 'dark' : 'light';
  const defaultMode = storedThemeMode || browserPreferredMode;
  const [mode, setMode] = useState<PaletteMode>(defaultMode);

  useEffect(() => {
    if (defaultMode === mode) {
      return;
    }

    setMode(defaultMode);
  }, [defaultMode]);

  // Bridges the brand config into plain global SCSS, which can't read JS/React
  // state on its own. See _custom.scss: rules there consume these via var(...).
  useEffect(() => {
    // TODO: Replace with brand configuration when available
    const colors = getAppBrandConfigs(mode);
    const root = document.documentElement;

    root.style.setProperty('--brand-primary', colors.primary);
    root.style.setProperty('--brand-secondary', colors.secondary);
  }, [mode]);

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: '"Roboto", sans-serif',
        },
        ...getDesignPalette(mode),
        ...getDesignComponents(mode),
      }),
    [mode],
  );

  const handleModeChange = (mode: PaletteMode) => {
    setValue(mode);
    setMode(mode);
  };

  return { theme, mode, setMode: handleModeChange };
};
